import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiAlert, mdiLoading, mdiPlay } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { AssetPicker } from '../AssetPicker'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'

interface TryOnAssetPanelProps {
  org: string
  uid: string
}

const show = (v: unknown) => (v === null || v === undefined ? '' : typeof v === 'string' ? v : JSON.stringify(v))

// "Try on an asset" (spec 2026-10-06-17): runs the saved derivation on the
// real values of one asset, its ancestors and its descendants, writing
// nothing, and shows the output, the matched row and the inputs read.
export function TryOnAssetPanel({ org, uid }: TryOnAssetPanelProps) {
  const { t } = useTranslation('derivations')
  const [asset, setAsset] = useState<{ uid: string; name: string }>({ uid: '', name: '' })
  const run = $api.useMutation('post', '/api/v1/organizations/{orgCode}/derivations/{derivationUid}/test')
  const result = run.data as any
  const error = run.error as any

  return (
    <div className="bg-card rounded-lg border border-border p-5 space-y-3" data-testid="derivation-try-on-asset">
      <div>
        <h3 className="text-sm font-semibold">{t('try.title')}</h3>
        <p className="text-xs text-muted-foreground">{t('try.help')}</p>
      </div>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[260px] flex-1">
          <AssetPicker
            org={org}
            value={asset.uid}
            valueLabel={asset.name}
            onChange={(assetUid, name) => setAsset({ uid: assetUid, name })}
            placeholder={t('try.placeholder')}
            testId="derivation-try-asset-picker"
          />
        </div>
        <Button
          variant="outline"
          disabled={!asset.uid || run.isPending}
          onClick={() => run.mutate({
            params: { path: { orgCode: org, derivationUid: uid } },
            body: { asset_uid: asset.uid } as any,
          })}
          data-testid="derivation-try-run"
        >
          <AppIcon path={run.isPending ? mdiLoading : mdiPlay} size="sm" className={run.isPending ? 'mr-1 motion-safe:animate-spin' : 'mr-1'} />
          {t('try.run')}
        </Button>
      </div>

      {error && (
        <p className="text-sm text-destructive" data-testid="derivation-try-error">{error.title || error.detail || String(error)}</p>
      )}

      {result && (
        <div className="space-y-2 text-sm" data-testid="derivation-try-result">
          {result.out_of_scope && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs" data-testid="derivation-try-out-of-scope">
              <AppIcon path={mdiAlert} size="xs" className="mt-0.5 flex-shrink-0 text-warning" />
              <span>{t('try.out_of_scope')}</span>
            </div>
          )}
          {result.error ? (
            <p className="text-destructive" data-testid="derivation-try-script-error">
              {t('test.error')}: <span className="font-mono text-xs">{result.error}</span>
            </p>
          ) : (
            <p className="flex items-center gap-2">
              <span className="text-muted-foreground">{t('test.output')}</span>
              {result.matched
                ? <code className="font-mono" data-testid="derivation-try-output">{show(result.output)}</code>
                : <span className="text-muted-foreground" data-testid="derivation-try-output">{t('test.no_answer')}</span>}
              {result.matched_rule && (
                <Badge variant="secondary">{t('test.matched_row', { priority: result.matched_rule.priority })}</Badge>
              )}
            </p>
          )}
          {Object.keys(result.inputs || {}).length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t('test.inputs_read')}</p>
              <dl className="mt-1 space-y-0.5 text-xs" data-testid="derivation-try-inputs">
                {Object.entries(result.inputs as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)).map(([path, value]) => (
                  <div key={path} className="flex justify-between gap-2">
                    <dt className="font-mono text-muted-foreground">{path}</dt>
                    <dd className="text-right break-all">{value === null || value === undefined ? <span className="text-muted-foreground">{t('test.absent')}</span> : show(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
